import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {HTMLElement[]} */
  #inertElements = [];

  /** Whether we engaged the shared (position:fixed) ScrollLock for this open. */
  #usedScrollLock = false;

  /** @type {{ startX: number, startY: number, currentY: number, startTime: number, axis: 'vertical' | null } | null} */
  #dragState = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupDragToDismiss();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#restoreBackgroundInteraction();
    this.#unlockScroll();
    this.#teardownDragToDismiss();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#trigger = summary instanceof HTMLElement ? summary : null;
      this.#disableBackgroundInteraction();
      this.#lockScroll();
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          trapFocus(details);
          this.#focusFirstInteractiveElement(details);
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Release the scroll lock now (not gated on the close animation), mirroring
    // dialog.js — an interrupted animation must never leave <body> stuck fixed.
    if (details === this.refs.details) this.#unlockScroll();

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          this.#restoreBackgroundInteraction();
          this.#trigger?.focus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
          this.#focusFirstInteractiveElement(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Keep the page behind the drawer out of the accessibility tree while the drawer is open.
   */
  #disableBackgroundInteraction() {
    const bodyChildren = Array.from(document.body.children).filter(
      (element) => element instanceof HTMLElement && !element.contains(this) && !element.hasAttribute('inert')
    );

    bodyChildren.forEach((element) => {
      element.setAttribute('inert', '');
    });

    this.#inertElements = bodyChildren;
  }

  #restoreBackgroundInteraction() {
    this.#inertElements.forEach((element) => {
      element.removeAttribute('inert');
    });
    this.#inertElements = [];
  }

  /**
   * Whether the "Full-height menu on iOS Safari" Dev setting is on. Driven by the
   * `data-ios-full-height` attribute the snippet renders from `settings`.
   * @returns {boolean}
   */
  get #isIosFullHeight() {
    return this.hasAttribute('data-ios-full-height');
  }

  /**
   * Lock background scroll on open.
   *
   * Full-height OFF: the `<details scroll-lock>` attribute already engages the
   * shared (position:fixed) ScrollLock via dialog.js — nothing to do here.
   *
   * Full-height ON: there is NO `scroll-lock` attribute (so the body is never made
   * position:fixed → iOS Safari keeps its toolbar collapsed). Mobile relies on the
   * backdrop's `touch-action:none` + the sheet's `overscroll-behavior:contain`.
   * Desktop still needs the shared ScrollLock because touch-action doesn't stop the
   * mouse wheel from scrolling the page behind the side drawer.
   */
  #lockScroll() {
    if (!this.#isIosFullHeight) return;
    if (window.innerWidth >= 750 && window.ScrollLock) {
      window.ScrollLock.lock();
      this.#usedScrollLock = true;
    }
  }

  /** Release the shared ScrollLock if (and only if) this open engaged it. */
  #unlockScroll() {
    if (this.#usedScrollLock && window.ScrollLock) {
      window.ScrollLock.unlock();
    }
    this.#usedScrollLock = false;
  }

  /**
   * @param {HTMLElement} container
   */
  #focusFirstInteractiveElement(container) {
    const focusTarget = container.querySelector(
      '.menu-drawer__close-button, a[href], button:enabled, summary, [tabindex]:not([tabindex^="-"])'
    );

    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus();
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  // ─── Drag-to-dismiss (mobile bottom-sheet) ───────────────────────────────

  #setupDragToDismiss() {
    const drawer = this.refs.menuDrawer;
    drawer.addEventListener('touchstart', this.#onDragStart, { passive: true });
    drawer.addEventListener('touchmove', this.#onDragMove, { passive: false });
    drawer.addEventListener('touchend', this.#onDragEnd, { passive: true });
    drawer.addEventListener('touchcancel', this.#onDragCancel, { passive: true });
  }

  #teardownDragToDismiss() {
    const drawer = this.refs.menuDrawer;
    drawer.removeEventListener('touchstart', this.#onDragStart);
    drawer.removeEventListener('touchmove', this.#onDragMove);
    drawer.removeEventListener('touchend', this.#onDragEnd);
    drawer.removeEventListener('touchcancel', this.#onDragCancel);
  }

  #onDragStart = (event) => {
    if (window.innerWidth >= 750) return;
    if (!this.isOpen) return;

    const touch = event.touches[0];
    const isOnStickyHeader = event.target instanceof Element && !!event.target.closest('.menu-drawer__sticky-header');
    const drawerScrollTop = this.refs.menuDrawer.scrollTop;

    // Allow drag from the sticky header at any scroll position;
    // elsewhere only when already at the top of scroll.
    if (!isOnStickyHeader && drawerScrollTop > 0) return;

    this.#dragState = {
      startX: touch.clientX,
      startY: touch.clientY,
      currentY: touch.clientY,
      startTime: Date.now(),
      // The sticky header is a dedicated drag handle, so lock to vertical immediately.
      // Elsewhere, wait for the first significant move to decide the gesture axis.
      axis: isOnStickyHeader ? 'vertical' : null,
    };
  };

  #onDragMove = (event) => {
    if (!this.#dragState) return;

    const touch = event.touches[0];
    const deltaY = touch.clientY - this.#dragState.startY;
    const deltaX = touch.clientX - this.#dragState.startX;

    // Decide the gesture axis once movement is significant. A horizontal-dominant
    // gesture (e.g. swiping a slider inside the drawer) releases the drag so the
    // native horizontal scroll is not hijacked by the dismiss handler.
    if (!this.#dragState.axis) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      if (absX < 8 && absY < 8) return;
      if (absX > absY) {
        this.#dragState = null;
        return;
      }
      this.#dragState.axis = 'vertical';
    }

    // If pulling up, abandon the drag
    if (deltaY <= 0) {
      this.#dragState = null;
      return;
    }

    event.preventDefault();
    this.#dragState.currentY = touch.clientY;

    const drawer = this.refs.menuDrawer;
    drawer.style.transform = `translateY(${deltaY}px)`;
    drawer.style.transition = 'none';

    // Fade backdrop proportionally
    const progress = Math.min(deltaY / drawer.offsetHeight, 1);
    const backdrop = this.querySelector('.menu-drawer__backdrop');
    if (backdrop instanceof HTMLElement) {
      backdrop.style.opacity = String(1 - progress);
      backdrop.style.transition = 'none';
    }
  };

  #onDragEnd = () => {
    if (!this.#dragState) return;

    const deltaY = this.#dragState.currentY - this.#dragState.startY;
    const elapsed = Date.now() - this.#dragState.startTime;
    const velocity = elapsed > 0 ? deltaY / elapsed : 0;
    this.#dragState = null;

    const drawer = this.refs.menuDrawer;
    const backdrop = this.querySelector('.menu-drawer__backdrop');
    const threshold = Math.min(150, drawer.offsetHeight * 0.25);
    const shouldDismiss = deltaY > threshold || velocity > 0.4;

    if (shouldDismiss) {
      const remaining = drawer.offsetHeight - deltaY;
      const duration = Math.max(150, Math.min(300, remaining * 0.8));
      const easing = 'var(--ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1))';

      drawer.style.transition = `transform ${duration}ms ${easing}`;
      drawer.style.transform = `translateY(${drawer.offsetHeight}px)`;
      if (backdrop instanceof HTMLElement) {
        backdrop.style.transition = `opacity ${duration}ms linear`;
        backdrop.style.opacity = '0';
      }

      drawer.addEventListener('transitionend', this.#onDismissTransitionEnd, { once: true });
    } else {
      this.#springBack(drawer, backdrop instanceof HTMLElement ? backdrop : null);
    }
  };

  #onDragCancel = () => {
    if (!this.#dragState) return;
    this.#dragState = null;

    const drawer = this.refs.menuDrawer;
    const backdrop = this.querySelector('.menu-drawer__backdrop');
    this.#springBack(drawer, backdrop instanceof HTMLElement ? backdrop : null);
  };

  /**
   * Animate drawer back to its resting (open) position.
   * @param {HTMLElement} drawer
   * @param {HTMLElement | null} backdrop
   */
  #springBack(drawer, backdrop) {
    const easing = 'var(--ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1))';
    drawer.style.transition = `transform 400ms ${easing}`;
    drawer.style.transform = 'translateY(0)';
    if (backdrop) {
      backdrop.style.transition = 'opacity 400ms linear';
      backdrop.style.opacity = '1';
    }

    const cleanup = () => {
      drawer.style.transition = '';
      drawer.style.transform = '';
      if (backdrop) {
        backdrop.style.transition = '';
        backdrop.style.opacity = '';
      }
    };
    drawer.addEventListener('transitionend', cleanup, { once: true });
  }

  /**
   * Called after the drag-dismiss animation completes.
   * Forces the drawer to the off-screen position before handing off to close(),
   * so close() finds no CSS transition to run (delta = 0) and cleans up state immediately.
   */
  #onDismissTransitionEnd = () => {
    const drawer = this.refs.menuDrawer;
    const backdrop = this.querySelector('.menu-drawer__backdrop');

    drawer.style.transition = '';
    // Keep the drawer visually off-screen via inline style while close() removes .menu-open.
    // close() computes the same translateY(100%) from CSS → no transition fires → instant cleanup.
    drawer.style.transform = 'translateY(100%)';

    if (backdrop instanceof HTMLElement) {
      backdrop.style.transition = '';
      backdrop.style.opacity = '';
    }

    this.close();

    // Remove inline style after close() has synchronously removed .menu-open,
    // so the CSS rule (.menu-drawer { transform: translateY(100%) }) takes over.
    requestAnimationFrame(() => {
      drawer.style.transform = '';
    });
  };
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}


